import { CircularProgress, Stack } from '@mui/material';
import { Viewer, Worker } from "@react-pdf-viewer/core";
import { useSnackbar } from 'notistack';
import { Costing, drawDocumentPdfPage, generateCostBreakupReportV2, generateDataSheet, IBasicDetails, Orientation, PDFConfigState } from 'pdf-generator';
import { PDFDocument, StandardFonts } from 'pdf-lib';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { SelectBoxOption } from '../../../interfaces';
import { useAppDispatch } from '../../../redux';
import { useLazyGetDataTableByIdQuery } from '../../../redux/services';
import { closeBackdrop, openBackdrop } from '../../../redux/slices/backdrop';
import { closeConfirmationDialog, openConfirmationDialog } from '../../../redux/slices/confirmationDialog';
import { TemplateResponse } from '../../../redux/slices/documentTemplate';
import { CheckListData } from '../../../screens/viewCheckList/viewCheckListDetail';
import { blobToBase64 } from '../../../utils';

// Import styles
import "@react-pdf-viewer/core/lib/styles/index.css";
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import "@react-pdf-viewer/highlight/lib/styles/index.css";

export interface DocProps {
    title?: string;
    headerTitle?: string;
    orientation: Orientation;
    paperSize: PaperSize;
    templateData: Partial<TemplateResponse & SelectBoxOption> | null;
    dataSheet?: Partial<CheckListData>;
    basicDetails?: IBasicDetails | Costing;
    details?: Partial<IBasicDetails> & { short_description?: string };
    type?: 'datasheet' | 'costbreakupreport';
    pricingID?: string;
    configState?: PDFConfigState;
    footerURL?: string;
    pdfDetails: {
        pdfInfo: string;
        setPdfInfo: React.Dispatch<React.SetStateAction<string>>
    }
}

export const paperSizes = [
    { label: '4A0', value: '4A0' },
    { label: '2A0', value: '2A0' },
    { label: 'A0', value: 'A0' },
    { label: 'A1', value: 'A1' },
    { label: 'A2', value: 'A2' },
    { label: 'A3', value: 'A3' },
    { label: 'A4', value: 'A4' },
    { label: 'A5', value: 'A5' },
    { label: 'A6', value: 'A6' },
    { label: 'A7', value: 'A7' },
    { label: 'A8', value: 'A8' },
    { label: 'A9', value: 'A9' },
    { label: 'A10', value: 'A10' },
    { label: 'B0', value: 'B0' },
    { label: 'B1', value: 'B1' },
    { label: 'B2', value: 'B2' },
    { label: 'B3', value: 'B3' },
    { label: 'B4', value: 'B4' },
    { label: 'B5', value: 'B5' },
    { label: 'B6', value: 'B6' },
    { label: 'B7', value: 'B7' },
    { label: 'B8', value: 'B8' },
    { label: 'B9', value: 'B9' },
    { label: 'B10', value: 'B10' },
    { label: 'C0', value: 'C0' },
    { label: 'C1', value: 'C1' },
    { label: 'C2', value: 'C2' },
    { label: 'C3', value: 'C3' },
    { label: 'C4', value: 'C4' },
    { label: 'C5', value: 'C5' },
    { label: 'C6', value: 'C6' },
    { label: 'C7', value: 'C7' },
    { label: 'C8', value: 'C8' },
    { label: 'C9', value: 'C9' },
    { label: 'C10', value: 'C10' },
    { label: 'RA0', value: 'RA0' },
    { label: 'RA1', value: 'RA1' },
    { label: 'RA2', value: 'RA2' },
    { label: 'RA3', value: 'RA3' },
    { label: 'RA4', value: 'RA4' },
    { label: 'SRA0', value: 'SRA0' },
    { label: 'SRA1', value: 'SRA1' },
    { label: 'SRA2', value: 'SRA2' },
    { label: 'SRA3', value: 'SRA3' },
    { label: 'SRA4', value: 'SRA4' },
    { label: 'Executive', value: 'EXECUTIVE' },
    { label: 'Folio', value: 'FOLIO' },
    { label: 'Legal', value: 'LEGAL' },
    { label: 'Letter', value: 'LETTER' },
    { label: 'Tabloid', value: 'TABLOID' },
    { label: 'ID1', value: 'ID1' },
];

export const jsPDFPaperSizes = [
    { label: 'A1', value: 'A1' },
    { label: 'A2', value: 'A2' },
    { label: 'A3', value: 'A3' },
    { label: 'A4', value: 'A4' },
    { label: 'A5', value: 'A5' },
    { label: 'Legal', value: 'Legal' },
    { label: 'Letter', value: 'Letter' },
    { label: 'Tabloid', value: 'Tabloid' },
];

export type PaperSize = typeof paperSizes[number]['value'];

export const pxToPoint = (px: number) => Math.ceil(px * 0.75);

const PdfView: React.FC<DocProps> = ({ title, templateData, dataSheet, basicDetails, details, pricingID = '', footerURL = '', configState, pdfDetails, ...rest }) => {
    const defaultLayoutPluginInstance = defaultLayoutPlugin();
    const { pdfInfo, setPdfInfo } = pdfDetails;

    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [getDataSheetById, dataSheetResponse] = useLazyGetDataTableByIdQuery({ refetchOnFocus: true, refetchOnReconnect: true })

    const [show, setShow] = React.useState(false);
    const [generatedURL, setURL] = React.useState<string>('');
    const [key, setKey] = React.useState("generated-pdf");

    const ID = rest.type === 'datasheet' ? (basicDetails as IBasicDetails)?.sequenceId : (basicDetails as Costing)?._id;

    const sequenceId = rest.type === 'datasheet' ? (basicDetails as IBasicDetails)?.sequenceId : (basicDetails as Costing)?.sequence_id;

    const updatedTitle = title || `Material Details Preview for ${(basicDetails as IBasicDetails)?.sequenceId || '...'}-r${basicDetails?.version ?? 0}`;

    const fetchDataSheet = async () => {
        const ds_id = (basicDetails as IBasicDetails)?.associations?.find((_: any) => _.object_type === "dataTableForm")?.object_details?.at(-1)?._id || "";
        if (ds_id) {
            setShow(false);
            setURL('');
            dispatch(openBackdrop("Fetching Data Sheet Detail..."));
            await getDataSheetById({
                dtTemplateType: "datasheet",
                dtInstanceType: "form",
                dtTemplateId: ds_id
            }).unwrap().catch((error: any) => {
                console.error("data sheet form fetch error: ", error)
                if (error.status === 404) {
                    dispatch(openConfirmationDialog({
                        title: "404 - Not Found",
                        body: "The Datasheet you are looking for is either not available or you don't have permission to edit it",
                        hideNegativeBtn: true,
                        hideCloseIcon: true,
                        positiveBtn: "Go Back",
                        onOk: () => {
                            dispatch(closeConfirmationDialog());
                            navigate(-1);
                        }
                    }))
                } else {
                    enqueueSnackbar(error.data?.title, { variant: "error" });
                }
            }).finally(() => {
                setShow(true);
                dispatch(closeBackdrop());
            })
        } else {
            setShow(true)
        }
    };

    const generateHFPdf = async () => {
        setPdfInfo('');
        dispatch(openBackdrop("Generating pdf..."));
        const pdfDoc = await PDFDocument.create();
        const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);

        const size = (templateData ? templateData?.paper_size || "A4" : "A4") as any;

        if (generatedURL && templateData) {
            // Documents
            try {
                const pdfBytes = await fetch(decodeURIComponent(generatedURL)).then((res) => res.arrayBuffer())
                const pdf = await PDFDocument.load(pdfBytes);
                await drawDocumentPdfPage(pdfDoc, helveticaFont, { sequence_id: sequenceId ?? "", version: (basicDetails?.version || details?.version) ?? 0, spare_part: false, vendor: { part_number: (((basicDetails as IBasicDetails)?.manufactureNo || details?.manufactureNo) ?? "-") as string, vendor_name: ((basicDetails as IBasicDetails)?.supplierName?.label ?? "-") as string, manufacturer_name: ((basicDetails as IBasicDetails)?.manufactureName || details?.manufactureName) ?? "-" }, short_description: ((basicDetails as IBasicDetails)?.short_description || details?.short_description) ?? "-" }, pdf, size, templateData ? templateData as any : {}, undefined, undefined, true, footerURL)
            } catch (error: any) {
                console.error(`error while fetching document: ${error}`)
            };
        }

        // Change the title
        pdfDoc.setTitle(updatedTitle);

        const pdfBytes = await pdfDoc.save();
        const bytes = new Uint8Array(pdfBytes);
        const blob = new Blob([bytes], { type: "application/pdf" });
        const docUrl = URL.createObjectURL(blob);
        const url = templateData ? docUrl : generatedURL;
        await fetch(url).then((res) => {
            return res.blob()
        }).then(async (res) => {
            try {
                const base64 = await blobToBase64(res) as string;
                setPdfInfo(base64);
            } catch (error) {
                console.error(`Error loading datasheet on preview: ${error}`)
            }
        }).catch((error: any) => {
            console.error(`Error loading datasheet on preview: ${error}`)
        }).finally(() => {
            dispatch(closeBackdrop());
        })
    };

    const prepareURL = async (hasTemplate: boolean) => {
        if (show) {
            switch (rest.type) {
                case 'datasheet':
                    setURL(generateDataSheet({ title: updatedTitle, dataSheet: dataSheetResponse?.data ?? dataSheet?.data, basicDetails: basicDetails as IBasicDetails, ...rest }));
                    break;

                case 'costbreakupreport':
                    setPdfInfo('');
                    setURL(await generateCostBreakupReportV2({ title: updatedTitle, headerTitle: rest.headerTitle, orientation: rest.orientation, paperSize: rest.paperSize, bomDetails: basicDetails as Costing, pricingId: pricingID, configState: configState as PDFConfigState, showFooter: hasTemplate ? false : true, footerURL }));
                    break;

                default:
                    break;
            }
        }
    };

    React.useEffect(() => {
        if (basicDetails) {
            fetchDataSheet();
        } else {
            setShow(true)
        }
        // eslint-disable-next-line
    }, [sequenceId, ID])

    React.useEffect(() => {
        setURL('');
    }, [templateData, sequenceId, rest.orientation, rest.paperSize, dataSheet, ID])

    React.useEffect(() => {
        setKey(templateData?._id ?? "");
    }, [templateData])

    React.useEffect(() => {
        setKey(sequenceId ?? "");
    }, [sequenceId])

    React.useEffect(() => {
        setKey(rest.orientation);
    }, [rest.orientation])

    React.useEffect(() => {
        setKey(rest.paperSize);
    }, [rest.paperSize])

    React.useEffect(() => {
        if (dataSheet) {
            setKey(dataSheet?.data?._id ?? "");
        }
    }, [dataSheet])

    React.useEffect(() => {
        if (generatedURL) {
            generateHFPdf();
        }
        // eslint-disable-next-line
    }, [generatedURL])

    React.useEffect(() => {
        prepareURL(templateData ? true : false);
        // eslint-disable-next-line
    }, [show, key])

    React.useEffect(() => {
        prepareURL(templateData ? true : false);
        // eslint-disable-next-line
    }, [JSON.stringify(configState)])

    React.useEffect(() => {
        if (rest.type === "costbreakupreport") {
            setKey("render-charts");
        }
        // eslint-disable-next-line
    }, [])

    return (
        (<Stack
            id={sequenceId ?? "viewer"}
            sx={{
                flexGrow: 1,
                overflow: "auto",
                height: "100%",
                width: "100%"
            }}>
            {/* {pdfInfo && <iframe ref={viewerRef} height={"100%"} width={"100%"} title={updatedTitle} src={pdfInfo}></iframe>} */}
            {pdfInfo && (
                <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                    <div style={{ width: "100%", height: "80vh" }}>
                        <Viewer
                            fileUrl={pdfInfo}
                            plugins={[
                                defaultLayoutPluginInstance,
                            ]}
                            onDocumentLoad={(e) => {

                            }}
                        />
                    </div>
                </Worker>
            )}
            {!pdfInfo && <Stack
                sx={{
                    height: "100%",
                    alignItems: 'center',
                    justifyContent: "center"
                }}><CircularProgress /></Stack>}
        </Stack>)
    );
}

export default PdfView